import Modal from "@modules/layout/modal";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useState, useMemo } from "react";
import { useRouter } from "next/router";
import { BsX } from "react-icons/bs";
import { DialCodeItem } from "@types";

interface DialCodeModalProps {
    open: boolean;
    onClose: () => void;
    dialcodes: DialCodeItem[];
    onSelect: (dialCode: DialCodeItem) => void;
}

const DialCodeModal: React.FC<DialCodeModalProps> = ({
    open,
    onClose,
    dialcodes,
    onSelect,
}) => {
    const t = useTranslations();
    const { locale } = useRouter();
    const [searchQuery, setSearchQuery] = useState("");

    const filteredDialCodes = useMemo(() => {
        if (!searchQuery.trim()) {
            return dialcodes;
        }

        const query = searchQuery.toLowerCase().trim();
        return dialcodes.filter((dialCode) => {
            const nameEn = dialCode.name_en.toLowerCase();
            const nameAr = dialCode.name_ar.toLowerCase();
            const dialCodeStr = dialCode.dialcode.toLowerCase();
            const code2 = dialCode.code2.toLowerCase();

            return (
                nameEn.includes(query) ||
                nameAr.includes(query) ||
                dialCodeStr.includes(query) ||
                code2.includes(query)
            );
        });
    }, [dialcodes, searchQuery]);

    const handleSelect = (dialCode: DialCodeItem) => {
        onSelect(dialCode);
        setSearchQuery(""); // Reset search when closing
    };

    const handleClose = () => {
        setSearchQuery(""); // Reset search when closing
        onClose();
    };

    return (
        <Modal open={open} onClose={handleClose} cancelable>
            <div className="md:w-[350px] w-full md:max-w-[400px] min-h-[300px] max-h-[500px] h-[500px] bg-white rounded-t-3xl md:rounded-lg p-4 flex flex-col">
                <div className="w-full mb-5">
                    <p className="flex-1 font-normal text-xl text-[#333] text-center mb-4">
                        {t("client_myaddresses_pickcountrycode")}
                    </p>

                    {/* Search Input */}
                    <div className="relative">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={t("Search country")}
                            className="w-full h-10 px-4 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery("")}
                                className="absolute right-3  top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <BsX size={20}/>
                            </button>
                        )}
                    </div>
                </div>

                {/* Results */}
                <div className="flex-1 overflow-y-auto">
                    {filteredDialCodes.length === 0 ? (
                        <div className="text-center text-gray-500 py-8">
                            {t("no_results_found")}
                        </div>
                    ) : (
                        filteredDialCodes.map((dialCode) => (
                            <div
                                className="flex flex-wrap h-14 items-center border-b cursor-pointer hover:bg-gray-50 transition-colors"
                                onClick={() => handleSelect(dialCode)}
                                key={dialCode.id}
                            >
                                <div className="w-6 h-6 rounded-full relative overflow-hidden">
                                    <Image
                                        src={dialCode.image.url}
                                        fill={true}
                                        alt={dialCode.name || ""}
                                    />
                                </div>
                                <p className="flex-1 font-normal text-sm text-[#333] flex justify-start px-3">
                                    {locale?.includes("ar") ? dialCode.name_ar : dialCode.name_en}
                                </p>
                                <p className="font-normal text-sm text-[#333]">
                                    {dialCode.dialcode}
                                </p>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default DialCodeModal; 